import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './GuildWarInstructions.css';

function sumBy(arr, key) {
  return arr.reduce((acc, cur) => acc + (cur[key] || 0), 0);
}

const thLabels = [
  { key: 'name', label: 'Name' },
  { key: 'winCount', label: 'Win' },
  { key: 'loseCount', label: 'Lose' },
  { key: 'winRate', label: 'Rate' },
  { key: 'bonusPoint', label: 'Point' },
];

export default function GuildWarRankingNihon() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [sortKey, setSortKey] = useState('winRate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [activeTab, setActiveTab] = useState('total'); // 'total' or 'season'
  const [guildNames, setGuildNames] = useState([]);
  const [selectedGuild, setSelectedGuild] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');

  // 認証トークンの確認
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    if (storedToken) {
      setAuthToken(storedToken);
      verifyToken(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  // トークンの検証
  const verifyToken = async (token) => {
    try {
      setLoading(true);
      const response = await axios.post(
        'https://rangers-api-3mfw.onrender.com/api/auth/verify-token',
        { token }
      );
      
      if (response.data.success) {
        setIsAuthenticated(true);
        setAuthToken(token);
        fetchGuildNames();
      } else {
        handleLogout();
      }
    } catch (error) {
      console.error('Token verification error:', error);
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  // ギルド名一覧取得
  const fetchGuildNames = async () => {
    try {
      const response = await axios.get(
        'https://rangers-api-3mfw.onrender.com/api/guilds/get-guild-names',
        {
          headers: {
            Authorization: `Bearer ${authToken}`
          }
        }
      );
      const guilds = response.data.filter(guild => guild.lastGuildWarRank >= 1 && guild.lastGuildWarRank <= 50);
      setGuildNames(guilds.map(guild => ({ gid: guild.gid, name: guild.name })));
    } catch (error) {
      console.error('Error fetching guild names:', error);
      if (error.response && error.response.status === 401) {
        handleLogout();
      }
    }
  };

  // ログアウト処理
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setAuthToken('');
    setIsAuthenticated(false);
    setSelectedGuild('');
    setHistory([]);
  };

  // ギルド履歴データを取得（初回はキャッシュAPI）
  const fetchGuildHistory = async (gid, useCache = true) => {
    if (!gid) return;
    
    try {
      setLoading(true);
      const url = useCache 
        ? `https://rangers-api-3mfw.onrender.com/api/guild-war-ranking-nihon/history/cache/${gid}`
        : `https://rangers-api-3mfw.onrender.com/api/guild-war-ranking-nihon/history/${gid}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setHistory(data.history || []);
      if (data.history && data.history.length > 0) {
        setSelectedSeason(data.history[0].season);
      }
    } catch (error) {
      console.error('Error fetching guild history:', error);
    } finally {
      setLoading(false);
    }
  };

  // ギルド選択時の処理
  useEffect(() => {
    if (selectedGuild && isAuthenticated) {
      fetchGuildHistory(selectedGuild, true);
    }
  }, [selectedGuild, isAuthenticated]);

  // 最新データ取得
  const fetchLive = () => {
    if (selectedGuild) {
      fetchGuildHistory(selectedGuild, false);
    }
  };

  // パスワード認証
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);
    
    try {
      const response = await axios.post(
        'https://rangers-api-3mfw.onrender.com/api/auth/verify-password',
        { password }
      );
      
      if (response.data.success) {
        const token = response.data.token;
        localStorage.setItem('authToken', token);
        setAuthToken(token);
        setIsAuthenticated(true);
        fetchGuildNames();
      } else {
        setAuthError('Password is incorrect');
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthError('Password is incorrect or server connection failed');
    } finally {
      setLoading(false);
      setPassword('');
    }
  };

  // メンバーごとの合計
  const memberMap = {};
  history.forEach(season => {
    season.members.forEach(m => {
      if (!memberMap[m.name]) {
        memberMap[m.name] = { name: m.name, winCount: 0, loseCount: 0, bonusPoint: 0 };
      }
      memberMap[m.name].winCount += m.winCount || 0;
      memberMap[m.name].loseCount += m.loseCount || 0;
      memberMap[m.name].bonusPoint += m.bonusPoint || 0;
    });
  });
  let memberTotals = Object.values(memberMap).map(m => ({
    ...m,
    winRate: (m.winCount + m.loseCount) > 0 ? (m.winCount / (m.winCount + m.loseCount)) * 100 : 0
  }));

  // ソート
  memberTotals.sort((a, b) => {
    let aVal = a[sortKey];
    let bVal = b[sortKey];
    if (sortKey === 'name') {
      aVal = aVal || '';
      bVal = bVal || '';
      return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    } else {
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    }
  });

  // 全体合計
  const totalWin = sumBy(memberTotals, 'winCount');
  const totalLose = sumBy(memberTotals, 'loseCount');
  const totalBonus = sumBy(memberTotals, 'bonusPoint');

  // セレクトボックス用
  const seasonOptions = history.map(season => season.season);

  // 選択中シーズンのデータ
  const selectedSeasonData = history.find(season => season.season === selectedSeason);
  let seasonMembers = (selectedSeasonData ? selectedSeasonData.members : []).map(m => ({
    ...m,
    winRate: (m.winCount + m.loseCount) > 0 ? (m.winCount / (m.winCount + m.loseCount)) * 100 : 0
  }));

  // シーズン表のソート
  seasonMembers.sort((a, b) => {
    let aVal = a[sortKey];
    let bVal = b[sortKey];
    if (sortKey === 'name') {
      aVal = aVal || '';
      bVal = bVal || '';
      return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    } else {
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    }
  });

  // ソートハンドラ
  const handleSort = key => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder(key === 'name' ? 'asc' : 'desc');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="guild-war-instructions">
        <div className="login-container">
          <h1>Guild War Ranking</h1>
          <form onSubmit={handlePasswordSubmit}>
            <label>Please enter the password:</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Submit'}
            </button>
            {authError && <p className="auth-error">{authError}</p>}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="guild-war-instructions">
      <style>{`
        .responsive-table {
          border: 2px solid #b0b0b0;
        }
        .responsive-table th, .responsive-table td {
          border: 1px solid #b0b0b0;
        }
      `}</style>
      <div className="select-group">
        <h1>Guild War Ranking</h1>
        <div>
          <label>Guild Name:</label>
          <select
            onChange={(e) => {
              setSelectedGuild(e.target.value);
            }}
            value={selectedGuild}
          >
            <option value="">Please select</option>
            {guildNames.map((guild, index) => (
              <option key={index} value={guild.gid}>{guild.name}</option>
            ))}
          </select>
        </div>
      </div>

      {!selectedGuild && (
        <div style={{ marginTop: 20 }}>
          <p>Please select a guild</p>
        </div>
      )}

      {selectedGuild && loading && (
        <div style={{ marginTop: 20 }}>
          <p>Loading data...</p>
        </div>
      )}

      {selectedGuild && !loading && history.length === 0 && (
        <div style={{ marginTop: 20 }}>
          <p>No data</p>
          <button className="fetch-button" style={{ margin: 0 }} onClick={fetchLive}>
            Get Latest Data
          </button>
        </div>
      )}

      {selectedGuild && !loading && history.length > 0 && (
        <div className="tab-navigation">
          <button
            className={`tab-button${activeTab === 'total' ? ' active' : ''}`}
            onClick={() => setActiveTab('total')}
          >
            Total 10 Seasons
          </button>
          <button
            className={`tab-button${activeTab === 'season' ? ' active' : ''}`}
            onClick={() => setActiveTab('season')}
          >
            Season by Season
          </button>
        </div>
      )}

      {selectedGuild && !loading && history.length > 0 && activeTab === 'total' && (
        <div>
          <h2 style={{ marginTop: 0 }}>Guild War Total 10 Seasons</h2>
          <ul>
            <li>Win: {totalWin}</li>
            <li>Lose: {totalLose}</li>
            <li>Point: {totalBonus}</li>
          </ul>
          <table className="responsive-table">
            <thead>
              <tr>
                <th></th>
                {thLabels.map(th => (
                  <th key={th.key} onClick={() => handleSort(th.key)}>
                    {th.label}
                    {sortKey === th.key ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {memberTotals.map((m, idx) => (
                <tr key={m.name}>
                  <td>{idx + 1}</td>
                  <td>{m.name}</td>
                  <td>{m.winCount}</td>
                  <td>{m.loseCount}</td>
                  <td>{m.winRate.toFixed(2)}%</td>
                  <td>{m.bonusPoint}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="fetch-button" style={{ margin: 0 }} onClick={fetchLive}>
            Get Latest Data
          </button>
        </div>
      )}

      {selectedGuild && !loading && history.length > 0 && activeTab === 'season' && (
        <div>
          <h2 style={{ marginTop: 0 }}>Season by Season</h2>
          <label style={{ display: 'block', marginBottom: 16 }}>
            Season Select:
            <select
              className="group-select"
              value={selectedSeason || ''}
              onChange={e => setSelectedSeason(Number(e.target.value))}
              style={{ marginLeft: 8 }}
            >
              {seasonOptions.map(season => (
                <option key={season} value={season}>{season}</option>
              ))}
            </select>
          </label>
          {selectedSeasonData ? (
            <>
              <table className="responsive-table">
                <thead>
                  <tr>
                    <th></th>
                    {thLabels.map(th => (
                      <th key={th.key} onClick={() => handleSort(th.key)}>
                        {th.label}
                        {sortKey === th.key ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {seasonMembers.map((m, idx) => (
                    <tr key={m.name}>
                      <td>{idx + 1}</td>
                      <td>{m.name}</td>
                      <td>{m.winCount}</td>
                      <td>{m.loseCount}</td>
                      <td>{m.winRate.toFixed(2)}%</td>
                      <td>{m.bonusPoint}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className="fetch-button" style={{ margin: 0 }} onClick={fetchLive}>
                Get Latest Data
              </button>
            </>
          ) : (
            <>
              <div>No data</div>
              <button className="fetch-button" style={{ margin: 0 }} onClick={fetchLive}>
                Get Latest Data
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
